'use client';

import { useState, type MouseEvent } from 'react';
import { FileArrowDown, CircleNotch } from '@phosphor-icons/react/dist/ssr';
import { toast } from 'sonner';
import { getMaterialSignedUrl } from '@/actions/materials';
import { materialIcon } from './materialUi';
import type { ClassMaterial } from '@/types';

/**
 * Download-Chips für die an eine Aufgabe verknüpften Bibliotheks-Materialien
 * (Schüler-Ansicht). Hell/flach, grosse Tap-Targets; signierte URL wird erst
 * beim Tippen erzeugt. Rendert nichts, wenn keine Materialien vorhanden sind.
 */
export function TaskMaterialList({ materials, className = '' }: { materials: ClassMaterial[]; className?: string }) {
  const [loadingId, setLoadingId] = useState<string | null>(null);
  if (!materials || materials.length === 0) return null;

  async function open(e: MouseEvent, m: ClassMaterial) {
    // Klick darf NICHT zur Karte durchblubbern (sonst öffnet sich die Aufgabe);
    // nur der Download startet.
    e.stopPropagation();
    if (loadingId) return;
    setLoadingId(m.id);
    try {
      const res = await getMaterialSignedUrl(m.id);
      if ('url' in res) window.open(res.url, '_blank', 'noopener,noreferrer');
      else toast.error(res.error);
    } finally {
      setLoadingId(null);
    }
  }

  return (
    <div className={`mt-3 flex flex-wrap gap-2 ${className}`}>
      {materials.map((m) => {
        const Icon = materialIcon(m.mime_type);
        const busy = loadingId === m.id;
        return (
          <button
            key={m.id}
            type="button"
            onClick={(e) => open(e, m)}
            disabled={busy}
            aria-label={`${m.file_name} herunterladen`}
            className="inline-flex max-w-full items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-soft transition-colors hover:border-brand-300 hover:bg-brand-50 active:scale-[0.98] disabled:opacity-60"
          >
            {busy
              ? <CircleNotch size={18} className="shrink-0 animate-spin text-brand-500" />
              : <Icon size={18} weight="duotone" className="shrink-0 text-brand-500" />}
            <span className="truncate">{m.file_name}</span>
            <FileArrowDown size={15} className="shrink-0 text-slate-400" />
          </button>
        );
      })}
    </div>
  );
}
